import { useEffect, useCallback } from "react";
import { hideOverlayOutline, showOverlayOutline } from "../apps/utilities";
import { useOverlayTooltip } from "./useOverlayTooltip";

interface UseInspectorModeOptions {
    inspectorMode: boolean;
    previewRef: React.RefObject<HTMLDivElement>;
    selectedEl: HTMLElement | null;
    setSelectedEl: (el: HTMLElement | null) => void;
    hoveredElRef: React.MutableRefObject<HTMLElement | null>;
    setInspectorTarget: (val: { id: string; nonce: number } | null) => void;
    handleNext: (step: number, clickedSetting: string) => void;
    bundleConfig: any;
}

export function useInspectorMode({
    inspectorMode,
    previewRef,
    selectedEl,
    setSelectedEl,
    hoveredElRef,
    setInspectorTarget,
    handleNext,
    bundleConfig,
}: UseInspectorModeOptions) {
    // wrapped click handler

    // Dont Remove : this hook to add overlay to the selected element when in inspector mode
    useOverlayTooltip(
        inspectorMode,
        selectedEl,
        selectedEl?.dataset.label || null,
        !!selectedEl,
        previewRef,
        bundleConfig,
    );

    const handleInspectorClick = (event: React.MouseEvent) => {
        if (!inspectorMode) return;

        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
            console.log("Inspector mode override - normal click allowed");
            return;
        }

        event.stopPropagation();
        event.preventDefault();

        const target = (event.target as HTMLElement).closest("[data-settings]") as HTMLElement | null;
        if (!target) return;

        const clickedSetting = target.dataset.settings;
        if (!clickedSetting) return;
        const [tabId, sectionId] = clickedSetting.split(":");

        console.log("Clicked section:", clickedSetting);

        // Remove overlay from previously selected element
        if (selectedEl && selectedEl !== target) {
            hideOverlayOutline(selectedEl);
        }

        hoveredElRef.current = null;
        setSelectedEl(target);

        // Jump to Design tab first
        handleNext(1, clickedSetting);
        // Set tabId

        setInspectorTarget({ id: tabId, nonce: Date.now() });

        // Scroll to section after tab switches
        setTimeout(() => {
            const sectionEl = document.getElementById(sectionId);

            if (!sectionEl) {
                console.warn(`Inspector: section "${sectionId}" not found. Skipping scroll.`);
                return; // gracefully do nothing
            }

            // Scroll the section into view
            sectionEl.scrollIntoView({ behavior: "smooth", block: "center" });

            // Apply highlight effect
            sectionEl.style.transition = "all 0.3s ease";
            sectionEl.style.transform = "scale(1.03)";
            sectionEl.style.boxShadow = "0 0 0 2px rgba(0, 123, 255, 0.5)";
            sectionEl.style.borderRadius = "3px"; // subtle rounded corners

            // Remove highlight after animation
            setTimeout(() => {
                sectionEl.style.transform = "";
                sectionEl.style.boxShadow = "";
                sectionEl.style.borderRadius = "";
            }, 1000);
        }, 100);
    };

    useEffect(() => {
        if (!inspectorMode || !previewRef.current) return;
        const container = previewRef.current;

        const handleMouseOver = (event: MouseEvent) => {
            const target = (event.target as HTMLElement).closest("[data-settings]") as HTMLElement | null;
            if (!target) return;

            // remove previous hover if different
            if (hoveredElRef.current && hoveredElRef.current !== selectedEl && hoveredElRef.current !== target) {
                hideOverlayOutline(hoveredElRef.current);
            }
            showOverlayOutline(target, {
                isSelected: target === selectedEl,
            });
            hoveredElRef.current = target;
        };

        const handleMouseLeave = () => {
            if (hoveredElRef.current && hoveredElRef.current !== selectedEl) {
                hideOverlayOutline(hoveredElRef.current);
                hoveredElRef.current = null;
            }
        };
        const handleDocumentDoubleClick = (event: MouseEvent) => {
            if (previewRef.current && !previewRef.current.contains(event.target as Node)) {
                // Clicked outside container
                if (selectedEl) {
                    hideOverlayOutline(selectedEl);
                    setSelectedEl(null);
                }
                if (hoveredElRef.current) {
                    hideOverlayOutline(hoveredElRef.current);
                    hoveredElRef.current = null;
                }
            }
        };

        document.addEventListener("mouseover", handleMouseOver);
        document.addEventListener("mouseleave", handleMouseLeave);
        document.addEventListener("dblclick", handleDocumentDoubleClick);

        return () => {
            document.removeEventListener("mouseover", handleMouseOver);
            document.removeEventListener("mouseleave", handleMouseLeave);
            document.removeEventListener("dblclick", handleDocumentDoubleClick);
        };
    }, [inspectorMode, selectedEl]);

    useEffect(() => {
        if (inspectorMode) {
            const wrapper =
                document.getElementById("placeholderStepsModalOverlayId") ||
                document.getElementById("bundlePreviewWrapper") ||
                document.getElementById("rightSectionContainerId");

            if (!wrapper) return;

            const handleMouseDown = (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                if (target.closest("select") && wrapper.contains(target)) {
                    if (!e.ctrlKey) {
                        // Normal inspector click ,block dropdown
                        e.preventDefault();
                    }
                    // else → ctrl+click → allow default select behavior
                }
            };

            window.addEventListener("mousedown", handleMouseDown, true);

            return () => {
                window.removeEventListener("mousedown", handleMouseDown, true);
            };
        } else {
            // Cleanup inspector overlays when inspector turns off
            if (hoveredElRef.current) {
                hideOverlayOutline(hoveredElRef.current);
                hoveredElRef.current = null;
            }
            if (selectedEl) {
                hideOverlayOutline(selectedEl);
                setSelectedEl(null);
            }
            setInspectorTarget(null);
        }
    }, [hoveredElRef, inspectorMode, selectedEl]);

    return { handleInspectorClick };
}
